import { useCallback, useEffect, useState } from 'react';

import { CompleteOperationType, StartOperationType } from 'model/alerts.model';
import { NotesManager } from 'model/notesManager.model';
import { GetNotesResponseType } from 'model/tasks.model';
import { ContactService } from 'service/contact.service';

export const useContactNotes = (
  id: string,
  startOperation: StartOperationType,
  completeOperation: CompleteOperationType,
): NotesManager<GetNotesResponseType> => {
  const [notes, setNotes] = useState<GetNotesResponseType[]>([]);
  const [loading, setLoading] = useState(false);

  const addNote = async (content: string) => {
    try {
      startOperation('upload', 'Adding note');
      await ContactService.addNote({
        entityId: id,
        noteContent: content,
        noteTitle: '',
      });
      completeOperation('upload', 'Note added successfully');
      await fetchNotes();
    } catch (err: any) {
      completeOperation('upload', `Failed to add note`, true);
    }
  };

  const updateNote = async (noteId: string, content: string) => {
    try {
      setLoading(true);
      startOperation('update', 'Updating note');
      await ContactService.updateNote({
        noteId,
        noteContent: content,
        noteTitle: '',
      });
      completeOperation('update', 'Note updated successfully');

      await fetchNotes();
    } catch (err: any) {
      completeOperation('upload', `Failed to updated the note`, true);
    } finally {
      setLoading(false);
    }
  };

  const deleteNote = async (noteId: string) => {
    try {
      setLoading(true);
      startOperation('delete', 'Deleting note');
      await ContactService.deleteNote(noteId);
      await fetchNotes();
      completeOperation('delete', 'Note deleted successfully');
    } catch (err: any) {
      completeOperation('delete', `Failed to delete the note`, true);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = useCallback(async () => {
    try {
      if (!id) return;
      setLoading(true);
      const res = await ContactService.getNotes(id);
      setNotes(res.notes || []);
    } catch (err: any) {
      completeOperation('fetch', `Failed to fetch the notes`, true);
    } finally {
      setLoading(false);
    }
  }, [id, setNotes]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  return { fetchNotes, notes, loading, deleteNote, updateNote, addNote };
};
